import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.CancellationException
import kotlin.js.Date

sealed class Screen {
    // Public — no login required
    data object Home : Screen()
    data object PublicSchedule : Screen()
    data object PublicTerminovaListina : Screen()
    data class PublicLeague(val leagueId: String, val leagueName: String) : Screen()
    data object PublicPlayers : Screen()
    data object PlayerOfRound : Screen()
    data object PublicRosters : Screen()
    data class ContentPage(val slug: String, val title: String) : Screen()
    // Turnaje (spec 17) — vlastní subdoména, ale tentýž build i tytéž obrazovky.
    data object PublicTournaments : Screen()
    data class PublicTournament(
        val slug: String, val title: String,
        val tab: PublicTournamentTab = PublicTournamentTab.DESCRIPTION,
    ) : Screen()

    // Administrace — gated behind login (see App())
    data object Seasons : Screen()
    data object Players : Screen()
    data object MatchEntry : Screen()
    /** `openNewsId`: open that news item straight in the editor (e.g. a draft just created from Statistiky). */
    data class ContentAdmin(val openNewsId: String? = null) : Screen()
    data object SeasonStats : Screen()
    data class Leagues(val seasonId: String, val seasonName: String) : Screen()
    data class TerminovaListina(val seasonId: String, val seasonName: String) : Screen()
    data class RozlosovaniTisk(val seasonId: String, val seasonName: String) : Screen()
    data class Tournaments(val seasonId: String, val seasonName: String) : Screen()
    data class Teams(val leagueId: String, val leagueName: String, val seasonId: String) : Screen()
    data class Roster(val teamId: String, val teamName: String, val seasonId: String, val back: Screen) : Screen()
    data class Schedule(val leagueId: String, val leagueName: String, val seasonId: String) : Screen()
    data class MatchResult(
        val matchId: String,
        val homeTeamId: String,
        val homeTeam: String,
        val awayTeamId: String,
        val awayTeam: String,
        val seasonId: String,
        val leagueName: String,
        val phase: String,
        val roundNumber: Int,
        val back: Screen,
    ) : Screen()
    data class Standings(val leagueId: String, val leagueName: String, val seasonId: String) : Screen()
}

private fun Screen.isAdmin(): Boolean = when (this) {
    is Screen.Home, is Screen.PublicSchedule, is Screen.PublicTerminovaListina, is Screen.PublicLeague, is Screen.PublicPlayers,
    is Screen.PlayerOfRound, is Screen.PublicRosters, is Screen.ContentPage,
    is Screen.PublicTournaments, is Screen.PublicTournament -> false
    else -> true
}

/** "Správa soutěže" (admin/superadmin): everything admin-side except Zápis o utkání and Obsah webu. */
private fun Screen.isCompetitionSection(): Boolean = when (this) {
    is Screen.Seasons, is Screen.Players, is Screen.Leagues, is Screen.TerminovaListina, is Screen.RozlosovaniTisk,
    is Screen.Tournaments, is Screen.Teams, is Screen.Roster, is Screen.Schedule, is Screen.Standings -> true
    is Screen.MatchResult -> back !is Screen.MatchEntry
    else -> false
}

private fun pageTitle(screen: Screen): String = when (screen) {
    is Screen.Home -> "Aktuálně"
    is Screen.PublicSchedule -> "Rozlosování"
    is Screen.PublicTerminovaListina -> "Termínová listina"
    is Screen.PublicLeague -> screen.leagueName
    is Screen.PublicPlayers -> "Žebříček jednotlivců"
    is Screen.PlayerOfRound -> "Hráč kola"
    is Screen.PublicRosters -> "Soupisky"
    is Screen.ContentPage -> screen.title
    is Screen.PublicTournaments -> "Turnaje"
    is Screen.PublicTournament -> screen.title
    is Screen.Seasons -> "Administrace — Sezóny"
    is Screen.Players -> "Administrace — Hráči"
    is Screen.MatchEntry -> "Administrace — Zápis o utkání"
    is Screen.ContentAdmin -> "Administrace — Obsah webu"
    is Screen.SeasonStats -> "Administrace — Statistiky"
    is Screen.Leagues -> "Administrace — Ligy"
    is Screen.TerminovaListina -> "Administrace — Termínová listina"
    is Screen.RozlosovaniTisk -> "Administrace — Tisk rozlosování"
    is Screen.Tournaments -> "Administrace — Turnaje"
    is Screen.Teams -> "Administrace — Týmy"
    is Screen.Roster -> "Administrace — Soupiska"
    is Screen.Schedule -> "Administrace — Rozpis"
    is Screen.MatchResult -> "Administrace — Zápas"
    is Screen.Standings -> "Administrace — Tabulka"
}

/**
 * Turnaje běží na vlastní subdoméně `turnaje.<doména>` (spec 17.2), ale je to jeden build a jeden
 * Pages projekt — o tom, co se zobrazí v menu a co je výchozí stránka, rozhoduje jen hostname.
 * Na localhostu (dev) subdoména neexistuje, proto jsou turnajové routy dostupné i pod `/turnaje`
 * a `/turnaj/<slug>` na hlavní doméně; kanonická adresa je ta na subdoméně.
 */
enum class SiteMode { League, Tournaments }

fun currentSiteMode(): SiteMode =
    if (window.location.hostname.startsWith("turnaje.")) SiteMode.Tournaments else SiteMode.League

/** Origin turnajové subdomény, nebo null, když ho z aktuálního hostu odvodit nejde (localhost, IP). */
fun tournamentsSiteOrigin(): String? {
    val host = window.location.hostname
    if (host.startsWith("turnaje.")) return null
    val bare = host.removePrefix("www.")
    if (!bare.contains('.')) return null
    if (bare.all { it.isDigit() || it == '.' }) return null
    return "${window.location.protocol}//turnaje.$bare"
}

/** Origin ligového webu při pohledu ze subdomény turnajů (jinak null). */
fun leagueSiteOrigin(): String? {
    val host = window.location.hostname
    if (!host.startsWith("turnaje.")) return null
    return "${window.location.protocol}//${host.removePrefix("turnaje.")}"
}

/** Kanonická veřejná adresa turnaje — na subdoméně, pokud ji lze odvodit, jinak dev fallback. */
fun tournamentPublicUrl(slug: String): String =
    tournamentsSiteOrigin()?.let { "$it/$slug" } ?: "/turnaj/$slug"

private val CZECH_DIACRITICS = mapOf(
    'á' to 'a', 'č' to 'c', 'ď' to 'd', 'é' to 'e', 'ě' to 'e', 'í' to 'i', 'ň' to 'n',
    'ó' to 'o', 'ř' to 'r', 'š' to 's', 'ť' to 't', 'ú' to 'u', 'ů' to 'u', 'ý' to 'y', 'ž' to 'z',
)

/** URL-safe slug for a league name, e.g. "3. liga" -> "3liga", used for deep-linkable league routes. */
fun slugify(name: String): String =
    name.lowercase().map { CZECH_DIACRITICS[it] ?: it }.joinToString("").filter { it.isLetterOrDigit() }

/** Path for public screens only; admin screens aren't deep-linked and return null (URL is left as-is). */
private fun pathForScreen(screen: Screen, mode: SiteMode): String? = when (screen) {
    // Na subdoméně turnajů je seznam turnajů kořen a detail visí přímo na slugu; na hlavní doméně
    // (a v devu) tytéž obrazovky žijí pod /turnaje a /turnaj/<slug>.
    is Screen.PublicTournaments -> if (mode == SiteMode.Tournaments) "/" else "/turnaje"
    is Screen.PublicTournament -> {
        val base = if (mode == SiteMode.Tournaments) "/${screen.slug}" else "/turnaj/${screen.slug}"
        base + screen.tab.path.takeIf { it.isNotEmpty() }?.let { "/$it" }.orEmpty()
    }
    is Screen.Home -> "/"
    is Screen.PublicSchedule -> "/rozlosovani"
    is Screen.PublicTerminovaListina -> "/terminova-listina"
    is Screen.PublicLeague -> "/${slugify(screen.leagueName)}"
    is Screen.PublicPlayers -> "/zebricek"
    is Screen.PlayerOfRound -> "/hrac-kola"
    is Screen.PublicRosters -> "/soupisky"
    is Screen.ContentPage -> "/stranka/${screen.slug}"
    else -> null
}

/** Resolves one of the fixed public routes; league/content-page routes are resolved separately. */
private fun staticScreenForPath(path: String, mode: SiteMode): Screen? = when (path.trim('/')) {
    "" -> if (mode == SiteMode.Tournaments) Screen.PublicTournaments else Screen.Home
    "turnaje" -> Screen.PublicTournaments
    "rozlosovani" -> Screen.PublicSchedule
    "terminova-listina" -> Screen.PublicTerminovaListina
    "zebricek" -> Screen.PublicPlayers
    "hrac-kola" -> Screen.PlayerOfRound
    "soupisky" -> Screen.PublicRosters
    else -> null
}

private fun screenForPath(
    path: String,
    leagues: List<SeasonLeagueDto>,
    pages: List<ContentPageDto>,
    mode: SiteMode,
): Screen {
    staticScreenForPath(path, mode)?.let { return it }
    val trimmed = path.trim('/')
    if (trimmed.startsWith("turnaj/") || mode == SiteMode.Tournaments) {
        val parts = trimmed.removePrefix("turnaj/").split('/')
        val slug = parts.first()
        val tab = PublicTournamentTab.entries.find { it.path == parts.getOrNull(1).orEmpty() }
            ?: PublicTournamentTab.DESCRIPTION
        return Screen.PublicTournament(slug, slug, tab)
    }
    // Content pages live under /stranka/<slug>; also accept the legacy /propozice URL.
    val pageSlug = when {
        trimmed.startsWith("stranka/") -> trimmed.removePrefix("stranka/")
        trimmed == "propozice" -> "propozice"
        else -> null
    }
    if (pageSlug != null) {
        val title = pages.find { it.slug == pageSlug }?.title ?: pageSlug
        return Screen.ContentPage(pageSlug, title)
    }
    leagues.find { slugify(it.name) == trimmed }?.let { return Screen.PublicLeague(it.id, it.name) }
    return Screen.Home
}

fun main() {
    renderComposable(rootElementId = "root") {
        App()
    }
}

@Composable
fun App() {
    // Tour has its own entry point and data lifecycle; never start league loaders on its routes.
    if (isTourSite()) {
        TourApp()
        return
    }
    var token by remember { mutableStateOf<String?>(loadPersistedToken()) }
    var sessionExpired by remember { mutableStateOf(false) }
    // Keep localStorage in sync so the session survives leaving the page/closing the tab
    // (restored above via loadPersistedToken, as long as the token hasn't expired).
    LaunchedEffect(token) { persistToken(token) }
    // An invite/recovery email link lands back here with the session in the URL hash (spec 9.9) —
    // intercept it before anything else renders so the user can pick a password.
    var pendingAuthToken by remember { mutableStateOf(parseAuthHashToken(window.location.hash)) }
    LaunchedEffect(Unit) {
        if (pendingAuthToken != null) window.history.replaceState(null, "", window.location.pathname)
    }
    if (pendingAuthToken != null) {
        SetPasswordScreen(accessToken = pendingAuthToken!!, onDone = { tok -> token = tok; pendingAuthToken = null })
        return
    }

    val siteMode = remember { currentSiteMode() }
    val initialPath = remember { window.location.pathname }
    var screen by remember { mutableStateOf<Screen>(staticScreenForPath(initialPath, siteMode) ?: Screen.Home) }
    var initialRouteApplied by remember { mutableStateOf(staticScreenForPath(initialPath, siteMode) != null) }
    var isPopNavigation by remember { mutableStateOf(false) }
    val claims: JwtClaims? = remember(token) { token?.let { decodeJwtClaims(it) } }
    val role = claims?.globalRole

    // Server-issued tokens carry their own exp; watching it client-side (not a security boundary --
    // RLS/RPC role checks are, same rationale as decodeJwtClaims) logs an idle admin out instead of
    // every subsequent request just starting to fail once the token expires.
    LaunchedEffect(token) {
        val exp = claims?.exp ?: return@LaunchedEffect
        val msUntilExpiry = exp * 1000.0 - Date().getTime()
        if (msUntilExpiry > 0) delay(msUntilExpiry.toLong())
        token = null
        sessionExpired = true
    }

    var activeSeason by remember { mutableStateOf<SeasonDto?>(null) }
    var activeLeagues by remember { mutableStateOf<List<SeasonLeagueDto>>(emptyList()) }
    var contentPages by remember { mutableStateOf<List<ContentPageDto>>(emptyList()) }
    // Shared per-season data is loaded only by screens that need it, retained across navigation.
    val leaguesBySeason = remember { mutableStateMapOf<String, List<SeasonLeagueDto>>() }
    val slotsBySeason = remember { mutableStateMapOf<String, List<SeasonRoundSlotDto>>() }
    val matchesBySeason = remember { mutableStateMapOf<String, List<MatchWithLeagueDto>>() }
    var seasonError by remember { mutableStateOf<String?>(null) }
    var dataLoaded by remember { mutableStateOf(false) }
    var navOpen by remember { mutableStateOf(false) }

    // Přepínání sezón ve veřejné sekci: uživatel může místo aktivní sezóny zobrazit archiv (finished).
    // Admin tím není dotčen -- ten dál pracuje s activeSeason. selected* drží data ZOBRAZOVANÉ sezóny;
    // ve výchozím stavu (selectedSeasonId == aktivní) jsou totožná s active*.
    var selectableSeasons by remember { mutableStateOf<List<SeasonDto>>(emptyList()) }
    var selectedSeasonId by remember { mutableStateOf<String?>(null) }
    var selectedLeagues by remember { mutableStateOf<List<SeasonLeagueDto>>(emptyList()) }
    var selectedSeasonLoading by remember { mutableStateOf(true) }
    var scheduleLoading by remember { mutableStateOf(false) }
    var scheduleError by remember { mutableStateOf<String?>(null) }
    var scheduleLoadAttempt by remember(selectedSeasonId, screen) { mutableStateOf(0) }
    val needsLeagueMenu = siteMode == SiteMode.League || screen.isAdmin()
    var tournaments by remember(selectedSeasonId) { mutableStateOf<List<TournamentDto>>(emptyList()) }
    var tournamentsError by remember(selectedSeasonId) { mutableStateOf<String?>(null) }
    var tournamentsLoadAttempt by remember { mutableStateOf(0) }

    LaunchedEffect(selectedSeasonId, tournamentsLoadAttempt) {
        val sid = selectedSeasonId ?: return@LaunchedEffect
        try {
            tournaments = Api.fetchTournaments(null, sid).filter { !it.isDraft }
            tournamentsError = null
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            tournamentsError = "Načtení turnajů selhalo."
        }
    }

    // Jediný turnaj otevřeme přímo i při příchodu na kořen turnajového webu.
    LaunchedEffect(screen, tournaments) {
        if (screen is Screen.PublicTournaments && tournaments.size == 1) {
            val t = tournaments.single()
            val target = Screen.PublicTournament(t.slug, t.title)
            window.history.replaceState(null, "", pathForScreen(target, siteMode))
            screen = target
        }
    }

    // Invalidate the shared schedule after a result save; the next visit reloads it on demand.
    suspend fun refreshPublicMatches(seasonId: String) {
        // Result saves happen in admin; the next public schedule visit fetches fresh data.
        matchesBySeason.remove(seasonId)
    }

    // Výběr ligy/kola v Zápisu o utkání se drží tady (ne v MatchEntryScreen), aby přežil odskok na
    // výsledek zápasu a zpět. Reset na aktuální kolo se dělá jen při vstupu přes menu (viz `navigate`).
    var matchEntryLeagueId by remember { mutableStateOf("") }
    var matchEntryRoundKey by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        try {
            coroutineScope {
                val seasons = async { Api.fetchSeasons(null) }
                val pages = async {
                    try {
                        if (siteMode == SiteMode.League) Api.fetchContentPages(null, includeBody = false) else emptyList()
                    } catch (e: Throwable) {
                        if (e is CancellationException) throw e
                        emptyList()
                    }
                }
                val allSeasons = seasons.await()
                activeSeason = allSeasons.firstOrNull { it.isActive }
                selectableSeasons = allSeasons.filter { it.isActive || it.status == "finished" }
                selectedSeasonId = activeSeason?.id ?: selectableSeasons.firstOrNull()?.id
                contentPages = pages.await()
            }
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            seasonError = "Načtení aktivní sezóny selhalo: ${e.apiMessage()}"
        }
        dataLoaded = true
    }

    // Přenačte data ZOBRAZOVANÉ sezóny (může být jiná než aktivní -- archiv) pro veřejné obrazovky.
    LaunchedEffect(selectedSeasonId, dataLoaded, needsLeagueMenu, screen.isAdmin()) {
        if (!dataLoaded) return@LaunchedEffect
        val sid = selectedSeasonId ?: run {
            selectedSeasonLoading = false
            return@LaunchedEffect
        }
        selectedSeasonLoading = true
        seasonError = null
        try {
            selectedLeagues = if (needsLeagueMenu) {
                leaguesBySeason[sid] ?: Api.fetchLeagues(null, sid).also { leaguesBySeason[sid] = it }
            } else emptyList()
            if (sid == activeSeason?.id) activeLeagues = selectedLeagues
            if (screen.isAdmin()) activeSeason?.id?.let { activeId ->
                activeLeagues = leaguesBySeason[activeId] ?: Api.fetchLeagues(null, activeId).also {
                    leaguesBySeason[activeId] = it
                }
            }
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            seasonError = "Načtení sezóny selhalo: ${e.apiMessage()}"
        } finally {
            selectedSeasonLoading = false
        }
    }

    LaunchedEffect(screen, selectedSeasonId, dataLoaded, scheduleLoadAttempt) {
        if (!dataLoaded) return@LaunchedEffect
        val sid = selectedSeasonId ?: return@LaunchedEffect
        val needsMatches = screen is Screen.PublicSchedule
        val needsSlots = needsMatches || screen is Screen.PublicTerminovaListina
        scheduleError = null
        if (!needsSlots) { scheduleLoading = false; return@LaunchedEffect }
        scheduleLoading = true
        try {
            coroutineScope {
                val slots = async { slotsBySeason[sid] ?: Api.fetchRoundSlots(null, sid) }
                val matches = async {
                    if (needsMatches) matchesBySeason[sid] ?: Api.fetchMatchesForSeason(null, sid) else emptyList()
                }
                slotsBySeason[sid] = slots.await()
                if (needsMatches) matchesBySeason[sid] = matches.await()
            }
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            scheduleError = "Načtení rozpisu selhalo: ${e.apiMessage()}"
        } finally {
            scheduleLoading = false
        }
    }

    // Resolves a deep-linked league/content-page route (e.g. /3liga, /stranka/propozice) once the
    // data those routes depend on has loaded. Fixed routes were already resolved synchronously above.
    LaunchedEffect(dataLoaded, selectedSeasonLoading) {
        if (dataLoaded && !selectedSeasonLoading && !initialRouteApplied) {
            screen = screenForPath(initialPath, selectedLeagues, contentPages, siteMode)
            initialRouteApplied = true
        }
    }

    // Keeps the address bar in sync with the current public screen (admin screens are left alone).
    // Skipped until the initial route is resolved, so a deep link like /3liga isn't clobbered back
    // to "/" while `screen` still holds its placeholder value during the async league lookup above.
    LaunchedEffect(screen) {
        if (!initialRouteApplied) {
            // no-op: still waiting on the league list to resolve the initial deep link
        } else if (isPopNavigation) {
            isPopNavigation = false
        } else {
            val path = pathForScreen(screen, siteMode)
            if (path != null && window.location.pathname != path) {
                window.history.pushState(null, "", path)
            }
        }
    }

    DisposableEffect(Unit) {
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            isPopNavigation = true
            screen = screenForPath(window.location.pathname, selectedLeagues, contentPages, siteMode)
        }
        window.addEventListener("popstate", listener)
        onDispose { window.removeEventListener("popstate", listener) }
    }

    // Zobrazovaná sezóna (výběr uživatele, fallback na aktivní). Archiv = cokoli jiného než aktuálně
    // běžící sezóna (ta je určená is_active / fetchActiveSeason, ne textovým statusem).
    val selectedSeason = selectableSeasons.find { it.id == selectedSeasonId } ?: activeSeason
    val isArchive = selectedSeason != null && selectedSeason.id != activeSeason?.id

    // Centrální navigace. Vstup do Zápisu o utkání PŘES MENU resetuje výběr ligy/kola (aby se
    // naskočilo na aktuální kolo); návrat z výsledku zápasu (screen == MatchResult) výběr zachová.
    val navigate: (Screen) -> Unit = { target ->
        if (target is Screen.MatchEntry && screen !is Screen.MatchResult) {
            matchEntryLeagueId = ""
            matchEntryRoundKey = ""
        }
        screen = target
        navOpen = false
    }

    Div(attrs = { classes(*(listOfNotNull("app-shell", "nav-open".takeIf { navOpen }).toTypedArray())) }) {
        // Mobile nav-open backdrop: tapping outside the sidebar closes it, same as picking a nav
        // item. Only rendered while open, so it never intercepts clicks (or exists at all) otherwise.
        if (navOpen) {
            Div(attrs = {
                classes("nav-backdrop")
                onClick { navOpen = false }
            }) {}
        }
        Sidebar(
            screen = screen,
            siteMode = siteMode,
            seasons = selectableSeasons,
            selectedSeasonId = selectedSeasonId,
            selectedSeason = selectedSeason,
            leagues = selectedLeagues,
            pages = contentPages,
            tournaments = tournaments,
            tournamentsError = tournamentsError,
            onRetryTournaments = { tournamentsLoadAttempt++ },
            isArchive = isArchive,
            onSelectSeason = { sid ->
                // Přepnutí sezóny resetuje obrazovku na Aktuálně -- leagueId z minulé sezóny by neseděl.
                selectedSeasonLoading = sid != selectedSeasonId
                selectedSeasonId = sid
                screen = if (siteMode == SiteMode.Tournaments) Screen.PublicTournaments else Screen.Home
                navOpen = false
            },
            onChange = navigate
        )

        Div(attrs = { classes("content") }) {
            var resolvedTournamentTitle by remember((screen as? Screen.PublicTournament)?.slug, selectedSeason?.id) {
                mutableStateOf<String?>(null)
            }
            Div(attrs = { classes("topbar") }) {
                Button(attrs = { classes("menu-button"); onClick { navOpen = !navOpen } }) { Text("☰") }
                H2 {
                    Text(when (val current = screen) {
                        is Screen.PublicTournaments -> "Turnaje" + (selectedSeason?.name?.let { " — $it" } ?: "")
                        is Screen.PublicTournament -> resolvedTournamentTitle ?: current.title.takeUnless { it == current.slug } ?: "Turnaj"
                        else -> pageTitle(current)
                    })
                }
                if (screen.isAdmin()) {
                    LoginBar(
                        token = token,
                        claims = claims,
                        sessionExpired = sessionExpired,
                        onToken = { token = it; if (it != null) sessionExpired = false }
                    )
                }
            }
            Div(attrs = { classes("page") }) {
                if (!dataLoaded || selectedSeasonLoading || !initialRouteApplied) {
                    Div(attrs = { classes("card") }) { Text("Načítám…") }
                } else when (val s = screen) {
                    is Screen.Home -> HomeScreen(
                        season = selectedSeason,
                        leagues = selectedLeagues,
                        error = seasonError,
                        loading = !dataLoaded || selectedSeasonLoading,
                        showNews = !isArchive,
                        onOverviewLoaded = { summary -> selectedSeasonId?.let { slotsBySeason[it] = summary.roundSlots } },
                    )
                    is Screen.PublicSchedule, is Screen.PublicTerminovaListina -> {
                        val sid = selectedSeasonId
                        val missing = sid != null && (slotsBySeason[sid] == null ||
                            (s is Screen.PublicSchedule && matchesBySeason[sid] == null))
                        if (scheduleError != null) {
                            Div(attrs = { classes("card") }) {
                                Text(scheduleError!!)
                                Button(attrs = { onClick { scheduleLoadAttempt++ } }) { Text("Zkusit načíst rozpis znovu") }
                            }
                        } else if (scheduleLoading || missing) {
                            Div(attrs = { classes("card") }) { Text("Načítám…") }
                        } else if (s is Screen.PublicSchedule) PublicScheduleScreen(
                            season = selectedSeason,
                            leagues = selectedLeagues,
                            matches = matchesBySeason[sid].orEmpty(),
                            roundSlots = slotsBySeason[sid].orEmpty(),
                        ) else PublicTerminovaListinaScreen(
                            season = selectedSeason, roundSlots = slotsBySeason[sid].orEmpty(), leagues = selectedLeagues,
                        )
                    }
                    is Screen.PublicLeague -> PublicLeagueScreen(leagueId = s.leagueId, leagueName = s.leagueName)
                    is Screen.PublicPlayers -> PublicPlayersScreen(season = selectedSeason)
                    is Screen.PlayerOfRound -> PlayerOfRoundScreen(seasonId = selectedSeason?.id, leagues = selectedLeagues)
                    is Screen.PublicRosters -> RostersScreen(season = selectedSeason)
                    is Screen.ContentPage -> ContentPageScreen(slug = s.slug)
                    is Screen.PublicTournaments -> PublicTournamentsScreen(
                        token = token,
                        season = selectedSeason,
                        onOpen = { t -> navigate(Screen.PublicTournament(t.slug, t.title)) },
                    )
                    is Screen.PublicTournament -> PublicTournamentScreen(
                        token = token,
                        season = selectedSeason,
                        slug = s.slug,
                        tab = s.tab,
                        onTabChange = { navigate(s.copy(tab = it)) },
                        onTitleResolved = { resolvedTournamentTitle = it },
                    )
                    else -> if (token == null) {
                        Div(attrs = { classes("card") }) {
                            H2 { Text("Přihlášení vyžadováno") }
                            Text("Administrace je dostupná jen po přihlášení (viz vpravo nahoře).")
                        }
                    } else {
                        AdminArea(
                            screen = s,
                            token = token,
                            role = role,
                            activeSeason = activeSeason,
                            activeLeagues = activeLeagues,
                            matchEntryLeagueId = matchEntryLeagueId,
                            onMatchEntryLeagueChange = { matchEntryLeagueId = it },
                            matchEntryRoundKey = matchEntryRoundKey,
                            onMatchEntryRoundChange = { matchEntryRoundKey = it },
                            onResultSaved = { seasonId -> refreshPublicMatches(seasonId) },
                            onChange = navigate
                        )
                    }
                }
            }
        }
    }

    ImageLightbox()
}

@Composable
fun Sidebar(
    screen: Screen,
    siteMode: SiteMode,
    seasons: List<SeasonDto>,
    selectedSeasonId: String?,
    selectedSeason: SeasonDto?,
    leagues: List<SeasonLeagueDto>,
    pages: List<ContentPageDto>,
    tournaments: List<TournamentDto>,
    tournamentsError: String?,
    onRetryTournaments: () -> Unit,
    isArchive: Boolean,
    onSelectSeason: (String) -> Unit,
    onChange: (Screen) -> Unit,
) {
    Div(attrs = { classes("sidebar") }) {
        Div(attrs = { classes("brand") }) {
            Div(attrs = { classes("brand-ball") }) {}
            Div {
                H1 { Text("GBL") }
                // Přepínač sezón (aktivní + archiv). Když je jen jedna sezóna, ukáže se prostý název.
                if (seasons.size > 1) {
                    Select(attrs = {
                        classes("season-switcher")
                        onChange { ev -> ev.value?.let { onSelectSeason(it) } }
                    }) {
                        seasons.forEach { s ->
                            Option(s.id, attrs = { if (s.id == selectedSeasonId) attr("selected", "selected") }) {
                                Text(s.name + if (s.isActive) " • aktivní" else "")
                            }
                        }
                    }
                } else {
                    P { Text(selectedSeason?.name ?: "Bez aktivní sezóny") }
                }
                if (isArchive) Div(attrs = { classes("archive-badge") }) { Text("Archiv") }
            }
        }
        Div(attrs = { classes("nav") }) {
            if (siteMode == SiteMode.Tournaments) {
                TournamentsSiteNav(screen = screen, tournaments = tournaments, onChange = onChange)
                if (tournamentsError != null) {
                    P { Text(tournamentsError) }
                    Button(attrs = { onClick { onRetryTournaments() } }) { Text("Zkusit znovu") }
                }
                return@Div
            }
            NavButton("Aktuálně", screen is Screen.Home) { onChange(Screen.Home) }
            NavButton("Rozlosování", screen is Screen.PublicSchedule) { onChange(Screen.PublicSchedule) }
            NavButton("Termínová listina", screen is Screen.PublicTerminovaListina) { onChange(Screen.PublicTerminovaListina) }
            leagues.forEach { league ->
                NavButton(league.name, screen is Screen.PublicLeague && screen.leagueId == league.id) {
                    onChange(Screen.PublicLeague(league.id, league.name))
                }
            }
            NavButton("Žebříček jednotlivců", screen is Screen.PublicPlayers) { onChange(Screen.PublicPlayers) }
            NavButton("Hráč kola", screen is Screen.PlayerOfRound) { onChange(Screen.PlayerOfRound) }
            NavButton("Soupisky", screen is Screen.PublicRosters) { onChange(Screen.PublicRosters) }
            tournaments.forEach { tournament ->
                if (tournamentsSiteOrigin() != null) {
                    A(href = tournamentPublicUrl(tournament.slug), attrs = { classes("nav-link") }) { Text(tournament.title) }
                } else {
                    NavButton(tournament.title, screen is Screen.PublicTournament && screen.slug == tournament.slug) {
                        onChange(Screen.PublicTournament(tournament.slug, tournament.title))
                    }
                }
            }
            if (tournamentsError != null) {
                P { Text(tournamentsError) }
                Button(attrs = { onClick { onRetryTournaments() } }) { Text("Zkusit znovu") }
            }
            // Configurable static pages -- jen mimo archiv (v archivu se custom stránky nezobrazují).
            if (!isArchive) {
                pages.forEach { page ->
                    NavButton(page.title, screen is Screen.ContentPage && screen.slug == page.slug) {
                        onChange(Screen.ContentPage(page.slug, page.title))
                    }
                }
            }
            Div(attrs = { classes("nav-divider") }) {}
            NavButton("Administrace", screen.isAdmin()) { onChange(Screen.MatchEntry) }
        }
    }
}

/** Menu subdomény turnajů (spec 17.2) — jen turnaje, odkaz zpět na ligu a administrace. */
@Composable
private fun TournamentsSiteNav(screen: Screen, tournaments: List<TournamentDto>, onChange: (Screen) -> Unit) {
    tournaments.forEach { tournament ->
        NavButton(tournament.title, screen is Screen.PublicTournament && screen.slug == tournament.slug) {
            onChange(Screen.PublicTournament(tournament.slug, tournament.title))
        }
        Div(attrs = { classes("nav-tournament-sections") }) {
            PublicTournamentTab.entries.filter { it != PublicTournamentTab.GROUPS || tournament.config.showGroups }.forEach { tab ->
                NavButton(tab.label, screen is Screen.PublicTournament && screen.slug == tournament.slug && screen.tab == tab) {
                    onChange(Screen.PublicTournament(tournament.slug, tournament.title, tab))
                }
            }
        }
    }
    leagueSiteOrigin()?.let { origin ->
        A(href = "$origin/", attrs = { classes("nav-link") }) { Text("Zpět na ligu") }
    }
    Div(attrs = { classes("nav-divider") }) {}
    NavButton("Administrace", screen.isAdmin()) { onChange(Screen.MatchEntry) }
}

@Composable
fun NavButton(label: String, active: Boolean, onClick: () -> Unit) {
    Button(attrs = {
        if (active) classes("active")
        onClick { onClick() }
    }) { Text(label) }
}

@Composable
fun LoginBar(token: String?, claims: JwtClaims?, sessionExpired: Boolean, onToken: (String?) -> Unit) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun submitLogin() {
        scope.launch {
            try {
                val resp = Api.login(email, password)
                if (resp.accessToken != null) {
                    onToken(resp.accessToken)
                    error = null
                } else {
                    error = resp.errorDescription ?: "Přihlášení selhalo"
                }
            } catch (e: Throwable) {
                error = "Přihlášení selhalo: ${e.message}"
            }
        }
    }

    Div(attrs = { classes("login-bar") }) {
        if (token == null) {
            if (sessionExpired) Div { Text("Přihlášení vypršelo, přihlas se prosím znovu.") }
            Input(type = InputType.Text) {
                value(email)
                onInput { email = it.value }
            }
            Input(type = InputType.Password) {
                value(password)
                onInput { password = it.value }
                onKeyDown { if (it.key == "Enter") submitLogin() }
            }
            Button(attrs = { onClick { submitLogin() } }) { Text("Přihlásit") }
        } else {
            Span(attrs = { classes("user-badge") }) {
                Text("${claims?.email ?: "?"} · role: ${claims?.globalRole ?: "none"}")
            }
            Button(attrs = { onClick { onToken(null) } }) { Text("Odhlásit") }
        }
        error?.let { Div { Text("Chyba: $it") } }
    }
}

@Composable
fun AdminArea(
    screen: Screen,
    token: String?,
    role: String?,
    activeSeason: SeasonDto?,
    activeLeagues: List<SeasonLeagueDto>,
    matchEntryLeagueId: String,
    onMatchEntryLeagueChange: (String) -> Unit,
    matchEntryRoundKey: String,
    onMatchEntryRoundChange: (String) -> Unit,
    onResultSaved: suspend (seasonId: String) -> Unit,
    onChange: (Screen) -> Unit,
) {
    // Skipped for the print view (Screen.RozlosovaniTisk) -- it's a standalone printable page with
    // its own "Zpět" button, and the sub-nav's buttons would otherwise show up in print/print preview.
    if (screen !is Screen.RozlosovaniTisk) {
        AdminSubNav(screen = screen, role = role, activeSeason = activeSeason, onChange = onChange)
    }

    when (screen) {
        is Screen.Seasons -> SeasonsScreen(
            token = token,
            role = role,
            onOpenLeagues = { seasonId, seasonName -> onChange(Screen.Leagues(seasonId, seasonName)) }
        )
        is Screen.Players -> PlayersScreen(token = token, role = role)
        // key: a different openNewsId (or none) must start from fresh editor state.
        is Screen.ContentAdmin -> key(screen.openNewsId) { ContentAdminScreen(token = token, role = role, openNewsId = screen.openNewsId) }
        is Screen.SeasonStats -> SeasonStatsScreen(
            token = token,
            role = role,
            activeSeason = activeSeason,
            onOpenNews = { newsId -> onChange(Screen.ContentAdmin(openNewsId = newsId)) },
        )
        is Screen.MatchEntry -> MatchEntryScreen(
            token = token,
            role = role,
            activeSeason = activeSeason,
            activeLeagues = activeLeagues,
            selectedLeagueId = matchEntryLeagueId,
            onSelectLeague = onMatchEntryLeagueChange,
            selectedRoundKey = matchEntryRoundKey,
            onSelectRound = onMatchEntryRoundChange,
            onMatchClick = { match, homeName, awayName, leagueName ->
                onChange(
                    Screen.MatchResult(
                        matchId = match.id,
                        homeTeamId = match.homeTeamId,
                        homeTeam = homeName,
                        awayTeamId = match.awayTeamId,
                        awayTeam = awayName,
                        seasonId = activeSeason?.id ?: "",
                        leagueName = leagueName,
                        phase = match.phase,
                        roundNumber = match.roundNumber,
                        back = Screen.MatchEntry,
                    )
                )
            }
        )
        is Screen.Leagues -> LeaguesScreen(
            token = token,
            role = role,
            seasonId = screen.seasonId,
            seasonName = screen.seasonName,
            onOpenTeams = { leagueId, leagueName -> onChange(Screen.Teams(leagueId, leagueName, screen.seasonId)) }
        )
        is Screen.TerminovaListina -> TerminovaListinaScreen(
            token = token,
            role = role,
            seasonId = screen.seasonId,
            onOpenPrint = { onChange(Screen.RozlosovaniTisk(screen.seasonId, screen.seasonName)) },
        )
        is Screen.RozlosovaniTisk -> RozlosovaniTiskScreen(
            token = token,
            seasonId = screen.seasonId,
            seasonName = screen.seasonName,
            onBack = { onChange(Screen.TerminovaListina(screen.seasonId, screen.seasonName)) },
        )
        is Screen.Tournaments -> TournamentsScreen(
            token = token,
            role = role,
            seasonId = screen.seasonId,
            seasonName = screen.seasonName,
        )
        is Screen.Teams -> TeamsScreen(
            token = token,
            role = role,
            leagueId = screen.leagueId,
            leagueName = screen.leagueName,
            seasonId = screen.seasonId,
            onOpenRoster = { teamId, teamName -> onChange(Screen.Roster(teamId, teamName, screen.seasonId, screen)) }
        )
        is Screen.Roster -> RosterScreen(
            token = token,
            role = role,
            teamId = screen.teamId,
            teamName = screen.teamName,
            seasonId = screen.seasonId,
            onBack = { onChange(screen.back) }
        )
        is Screen.Schedule -> ScheduleScreen(
            token = token,
            leagueId = screen.leagueId,
            leagueName = screen.leagueName,
            seasonId = screen.seasonId,
            onMatchClick = { match, homeName, awayName ->
                onChange(
                    Screen.MatchResult(
                        matchId = match.id,
                        homeTeamId = match.homeTeamId,
                        homeTeam = homeName,
                        awayTeamId = match.awayTeamId,
                        awayTeam = awayName,
                        seasonId = screen.seasonId,
                        leagueName = screen.leagueName,
                        phase = match.phase,
                        roundNumber = match.roundNumber,
                        back = screen,
                    )
                )
            }
        )
        is Screen.MatchResult -> MatchResultScreen(
            matchId = screen.matchId,
            homeTeamId = screen.homeTeamId,
            homeTeam = screen.homeTeam,
            awayTeamId = screen.awayTeamId,
            awayTeam = screen.awayTeam,
            seasonId = screen.seasonId,
            leagueName = screen.leagueName,
            phase = screen.phase,
            roundNumber = screen.roundNumber,
            token = token,
            role = role,
            onResultSaved = {
                // The save has already succeeded. Invalidate public data for the next visit;
                // a callback failure must not make the result screen claim the save failed.
                try {
                    onResultSaved(screen.seasonId)
                } catch (_: Throwable) {
                    // A later page reload will fetch the persisted result directly.
                }
            },
            onDone = {
                onChange(screen.back)
            }
        )
        is Screen.Standings -> StandingsScreen(token = token, leagueId = screen.leagueId, leagueName = screen.leagueName)
        else -> {}
    }
}

@Composable
fun AdminSubNav(screen: Screen, role: String?, activeSeason: SeasonDto?, onChange: (Screen) -> Unit) {
    val seasonCtx: Pair<String, String>? = when (screen) {
        is Screen.Leagues -> screen.seasonId to screen.seasonName
        is Screen.TerminovaListina -> screen.seasonId to screen.seasonName
        is Screen.Tournaments -> screen.seasonId to screen.seasonName
        is Screen.Teams -> screen.seasonId to ""
        is Screen.Roster -> screen.seasonId to ""
        is Screen.Schedule -> screen.seasonId to ""
        is Screen.MatchResult -> screen.seasonId to ""
        is Screen.Standings -> screen.seasonId to ""
        else -> null
    }
    val leagueCtx: Triple<String, String, String>? = when (screen) {
        is Screen.Teams -> Triple(screen.leagueId, screen.leagueName, screen.seasonId)
        is Screen.Schedule -> Triple(screen.leagueId, screen.leagueName, screen.seasonId)
        is Screen.Standings -> Triple(screen.leagueId, screen.leagueName, screen.seasonId)
        else -> null
    }

    // Zápis o utkání first and open to every logged-in role (it's the most common entry point);
    // Obsah webu and Správa soutěže are admin/superadmin only.
    Div(attrs = { classes("actions") }) {
        Button(attrs = { onClick { onChange(Screen.MatchEntry) } }) { Text("Zápis o utkání") }
        if (role.canManage()) {
            Button(attrs = { onClick { onChange(Screen.ContentAdmin()) } }) { Text("Obsah webu") }
            Button(attrs = { onClick { onChange(Screen.SeasonStats) } }) { Text("Statistiky") }
            Button(attrs = {
                onClick {
                    // The competition area starts in the current season, making all of its
                    // season-scoped sections available at once. Sezóny remains the place to
                    // choose a different season.
                    val season = activeSeason
                    onChange(if (season != null) Screen.Leagues(season.id, season.name) else Screen.Seasons)
                }
            }) { Text("Správa soutěže") }
        }
    }

    // Everything else (Sezóny, Hráči, and the season/league drill-down) lives under Správa soutěže.
    if (role.canManage() && screen.isCompetitionSection()) {
        Div(attrs = { classes("actions") }) {
            Button(attrs = { onClick { onChange(Screen.Seasons) } }) { Text("Sezóny") }
            Button(attrs = { onClick { onChange(Screen.Players) } }) { Text("Hráči") }
            if (seasonCtx != null) {
                val (seasonId, seasonName) = seasonCtx
                Button(attrs = { onClick { onChange(Screen.Leagues(seasonId, seasonName)) } }) { Text("Ligy") }
                Button(attrs = { onClick { onChange(Screen.TerminovaListina(seasonId, seasonName)) } }) { Text("Termínová listina") }
                Button(attrs = { onClick { onChange(Screen.Tournaments(seasonId, seasonName)) } }) { Text("Turnaje") }
            }
            if (leagueCtx != null) {
                val (leagueId, leagueName, seasonId) = leagueCtx
                Button(attrs = { onClick { onChange(Screen.Teams(leagueId, leagueName, seasonId)) } }) { Text("Týmy") }
                Button(attrs = { onClick { onChange(Screen.Schedule(leagueId, leagueName, seasonId)) } }) { Text("Rozpis") }
                Button(attrs = { onClick { onChange(Screen.Standings(leagueId, leagueName, seasonId)) } }) { Text("Tabulka") }
            }
        }
    }
}
